// src/components/Blog/Blog.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import "./Blog.css";

const API_BASE_URL = "/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

// 🔍 Debug interceptors (safe to keep)
api.interceptors.request.use(
  (config) => {
    console.log(
      `🚀 ${config.method?.toUpperCase()} → ${config.baseURL}${config.url}`
    );
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    console.log(`✅ Response ${response.status} from ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error("❌ API Error:", error.message);
    return Promise.reject(error);
  }
);

const Blog = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 🔁 Fallback articles
  const getFallbackData = () => [
    {
      id: 1,
      title: "Top 5 Neighborhoods to Invest in 2024",
      excerpt:
        "Discover the most promising neighborhoods for real estate investment this year.",
      image:
        "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=800&q=80",
      date: "March 10, 2024",
      author: "Atlas Spaces",
      url: "#",
    },
    {
      id: 2,
      title: "Understanding Mortgage Rates in Today’s Market",
      excerpt: "A simple guide to mortgage rates and how they impact buyers.",
      image:
        "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=800&q=80",
      date: "March 12, 2024",
      author: "Atlas Spaces",
      url: "#",
    },
    {
      id: 3,
      title: "Commercial Real Estate Trends to Watch",
      excerpt: "Key trends shaping commercial real estate investments.",
      image:
        "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=800&q=80",
      date: "March 15, 2024",
      author: "Atlas Spaces",
      url: "#",
    },
  ];

  // 📰 Fetch News
  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await api.get("/news");
        const data = response.data;

        if (data.success && Array.isArray(data.articles)) {
          const mapped = data.articles.map((article, index) => ({
            id: index + 1,
            title: article.title,
            excerpt: article.description || "Read more about this article...",
            image:
              article.image ||
              "https://images.unsplash.com/photo-1560518883-ce09059eeffa",
            date: new Date(article.publishedAt).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "long",
              year: "numeric",
            }),
            author: article.source?.name || "News Source",
            url: article.url,
          }));

          setBlogPosts(mapped);
        } else {
          setBlogPosts(getFallbackData());
        }
      } catch (err) {
        console.error("News fetch failed:", err);
        setError("Unable to load news. Showing sample articles.");
        setBlogPosts(getFallbackData());
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  const handleReadMore = (url, e) => {
    e.preventDefault();
    if (url && url !== "#") {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  // ⏳ Loading UI
  if (loading) {
    return (
      <section className="blog">
        <div className="container">
          <h5>Latest News & Blog</h5>
          <p>Loading real estate news...</p>
        </div>
      </section>
    );
  }

  // ⚠️ Error UI
  if (error) {
    return (
      <section className="blog">
        <div className="container">
          <h5>Latest News & Blog</h5>
          <p className="error-text">{error}</p>
        </div>
      </section>
    );
  }

  // ✅ Normal Render
  return (
    <section className="blog">
      <div className="container">
        <div className="section-title">
          <h5>Latest News & Blog</h5>
          <p>Real estate insights & market trends</p>
        </div>

        <div className="blog-grid">
          {blogPosts.map((post) => (
            <article key={post.id} className="blog-card">
              <div className="blog-image">
                <img src={post.image} alt={post.title} />
              </div>

              <div className="blog-content">
                <div className="blog-meta">
                  <span className="date">{post.date}</span>
                  <span className="author">{post.author}</span>
                </div>

                <h3>{post.title}</h3>
                <p>{post.excerpt}</p>

                <a
                  href={post.url}
                  className="read-more"
                  onClick={(e) => handleReadMore(post.url, e)}
                >
                  Read More →
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Blog;
